import { StyleSheet } from 'react-native';

/**
 * Deck ("One at a time") styles. Colors intentionally mirror the all-games card
 * (GameInlineCard.styles) so the deck and the list read as the same product surface
 * (specs/challenge-flow-redesign — item #3 / §Display Requirements). Layout/spacing unchanged.
 */
const C = {
  cardBg: '#1A1233',
  cardBorder: 'rgba(255,255,255,0.06)',
  inset: '#111827',
  chipBg: '#1F2937',
  chipBorder: '#374151',
  textDefault: '#F7FAFC',
  textDim: '#9CA3AF',
  textDim2: '#D1D5DB',
  textFaint: '#6B7280',
  amber: '#F59E0B',
  gold: '#FCD34D',
  primary: '#A78BFA',
  primaryLight: '#C4B5FD',
  segEmpty: '#1F2937',
} as const;

export const deckStyles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
  },
  // Fixed bands are trimmed hard so the default card (1 row of odds tiles) fits a short phone-web
  // viewport WITH the nav pinned (per user: Next stays sticky; shrink things instead of scrolling).
  progressRow: {
    flexDirection: 'row',
    gap: 6,
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  segment: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    backgroundColor: C.segEmpty,
  },
  // A filled segment = a pick locked in (picks-based progress, not deck position).
  segmentDone: {
    backgroundColor: C.gold,
  },
  idxRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: 4,
  },
  idxLabel: {
    color: C.textDim,
    fontSize: 12,
  },
  scoreLabel: {
    color: C.textDim,
    fontSize: 12,
  },
  scoreValue: {
    color: C.amber,
    fontWeight: '800',
    fontSize: 15,
  },
  cardScroll: {
    flex: 1,
    // Web flex fix: without minHeight:0 a flex ScrollView won't shrink below its content, so on
    // short mobile viewports it overflowed the pinned nav/footer and the card clipped mid-content.
    // With the trimmed chrome the default card fits; this scroll is the fallback for tall markets
    // (Correct Score, O/U grids) and when the picks box appears.
    minHeight: 0,
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: C.cardBg,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: C.cardBorder,
    padding: 10,
    gap: 8,
    marginBottom: 4,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  league: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.8,
    color: C.textDim,
  },
  time: {
    fontSize: 12,
    color: C.textDim2,
  },
  teamsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  team: {
    flex: 1,
    alignItems: 'center',
    gap: 4,
  },
  teamName: {
    fontSize: 13,
    fontWeight: '700',
    color: C.textDefault,
    textAlign: 'center',
  },
  vs: {
    fontSize: 13,
    fontWeight: '800',
    color: C.textFaint,
  },
  loading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
  },
  loadingText: {
    color: C.textDim,
    fontSize: 13,
  },
  // Fixed-height single row (no title, no wrapping) — picking must not grow the card past the fold.
  picksBox: {
    flexGrow: 0,
    backgroundColor: C.inset,
    borderRadius: 10,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  picksRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  pickChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: C.chipBg,
    borderWidth: 1,
    borderColor: C.chipBorder,
    borderRadius: 9999,
    paddingVertical: 3,
    paddingLeft: 9,
    paddingRight: 4,
  },
  pickChipText: {
    fontSize: 10.5,
    color: C.textDim2,
  },
  pickChipOdds: {
    fontSize: 10.5,
    fontWeight: '700',
    color: C.amber,
  },
  pickChipRemove: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: C.inset,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pickChipRemoveText: {
    fontSize: 11,
    color: C.textFaint,
    lineHeight: 13,
  },
  navRow: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 2,
  },
  prevBtn: {
    minHeight: 32,
    paddingHorizontal: 16,
    borderRadius: 9999,
    borderWidth: 1,
    borderColor: C.chipBorder,
    alignItems: 'center',
    justifyContent: 'center',
  },
  prevBtnText: {
    color: C.textDim,
    fontSize: 13,
    fontWeight: '500',
  },
  nextBtn: {
    minHeight: 34,
    paddingHorizontal: 22,
    borderRadius: 9999,
    borderWidth: 1,
    borderColor: C.primary,
    backgroundColor: 'rgba(167,139,250,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nextBtnText: {
    color: C.primaryLight,
    fontSize: 13.5,
    fontWeight: '600',
  },
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    paddingHorizontal: 32,
  },
  emptyEmoji: {
    fontSize: 44,
  },
  endTitle: {
    fontSize: 19,
    fontWeight: '800',
    color: C.textDefault,
    textAlign: 'center',
  },
  endSub: {
    fontSize: 13.5,
    color: C.textDim,
    textAlign: 'center',
    lineHeight: 20,
  },
  secondaryBtn: {
    minHeight: 44,
    paddingHorizontal: 20,
    borderRadius: 10,
    backgroundColor: C.chipBg,
    borderWidth: 1,
    borderColor: C.chipBorder,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
  },
  secondaryBtnText: {
    color: C.textDefault,
    fontSize: 14,
    fontWeight: '600',
  },
  ghostBtn: {
    minHeight: 44,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: C.chipBorder,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
  },
  ghostBtnText: {
    color: C.textDim,
    fontSize: 14,
    fontWeight: '600',
  },
});
